import { styled } from 'styled-system/jsx'

const Card = styled('article', {
  base: {
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#EAE4F8',
    borderRadius: 'lg',
    padding: '0',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 18px 48px -28px rgba(101, 56, 224, 0.22)',
    transition: 'transform {durations.fast} {easings.appleStandard}',
    _hover: { transform: 'translateY(-2px)' },
  },
})

const HeroImage = styled('div', {
  base: {
    width: '100%',
    aspectRatio: '16 / 9',
    backgroundColor: 'quickpass.surfaceCool',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    padding: '12px',
  },
})

const HeroPlaceholder = styled('span', {
  base: {
    fontSize: '32px',
    margin: 'auto',
  },
})

const StatusPill = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    paddingX: '10px',
    paddingY: '5px',
    borderRadius: 'pill',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    color: 'quickpass.ink',
  },
  variants: {
    tone: {
      pending: {
        backgroundColor: 'quickpass.primary',
        color: '#FFFFFF',
      },
      viewed: {
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        color: 'quickpass.inkSoft',
      },
      done: {
        backgroundColor: 'rgba(46, 184, 137, 0.92)',
        color: '#FFFFFF',
      },
      passed: {
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        color: 'quickpass.inkSoft',
      },
    },
  },
})

const MatchPill = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    paddingX: '10px',
    paddingY: '5px',
    borderRadius: 'pill',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    color: '#FFFFFF',
  },
})

const Body = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '16px 18px',
  },
})

const SenderRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: 'quickpass.primary',
  },
})

const SenderDot = styled('span', {
  base: {
    width: '6px',
    height: '6px',
    borderRadius: 'pill',
    backgroundColor: 'quickpass.primary',
  },
})

const Title = styled('h3', {
  base: {
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.2px',
    lineHeight: '1.3',
  },
})

const MetaLine = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
  },
})

const PriceLine = styled('div', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '700',
    color: 'quickpass.ink',
    letterSpacing: '-0.3px',
  },
})

const Message = styled('blockquote', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.ink',
    margin: '0',
    paddingLeft: '12px',
    borderLeft: '3px solid',
    borderColor: 'quickpass.primary',
    lineHeight: '1.55',
  },
})

const Reasons = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    paddingTop: '4px',
  },
})

const ReasonChip = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '600',
    color: 'quickpass.primary',
    backgroundColor: 'rgba(128, 84, 255, 0.08)',
    border: '1px solid rgba(128, 84, 255, 0.25)',
    borderRadius: 'pill',
    paddingX: '10px',
    paddingY: '4px',
  },
})

const PrivacyNote = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
    paddingTop: '6px',
    borderTop: '1px dashed',
    borderColor: '#EAE4F8',
    lineHeight: '1.45',
  },
})

const Actions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingTop: '4px',
  },
})

const InterestedButton = styled('button', {
  base: {
    flex: '1',
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '16px',
    paddingY: '10px',
    cursor: 'pointer',
    transition:
      'transform {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _hover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 28px -10px rgba(128, 84, 255, 0.5)',
    },
    _disabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
      _hover: { transform: 'none', boxShadow: 'none' },
    },
  },
})

const PassButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '9px',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: { borderColor: 'quickpass.warmth', color: 'quickpass.warmth' },
    _disabled: { opacity: '0.5', cursor: 'not-allowed' },
  },
})

const ResolvedNote = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.success',
    fontWeight: '600',
    paddingTop: '4px',
  },
})

const SourceLink = styled('a', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    color: 'quickpass.primary',
    textDecoration: 'none',
    _hover: { textDecoration: 'underline' },
  },
})

export {
  Card,
  HeroImage,
  HeroPlaceholder,
  StatusPill,
  MatchPill,
  Body,
  SenderRow,
  SenderDot,
  Title,
  MetaLine,
  PriceLine,
  Message,
  Reasons,
  ReasonChip,
  PrivacyNote,
  Actions,
  InterestedButton,
  PassButton,
  ResolvedNote,
  SourceLink,
}
